import { useMemo, useState } from 'react';
import { cn } from '@/lib/cn';
import {
  activeFilterCount, EMPTY_LOOKUP_FILTERS,
  type LookupAxis, type LookupFilters,
} from '@lib/lookup-axes';

/**
 * SlicerRail - the browse-all Lookup tabs' filter rail (Portal OpsSlicerRail port, ClariCommand
 * paper edition). Generic + data-driven: the section passes its ordered axes (option pools drawn
 * from the loaded rows) and the selection map; the rail renders contiguous same-group runs under
 * one uppercase header, one collapsible block per axis. Any axis whose option pool is empty
 * self-hides. Within an axis selected chips OR together; across axes they AND (the section's
 * matcher owns the semantics - the rail only edits the selection map).
 *
 * Collapsible to a slim spine (state remembered per browser, shared across tabs via one localStorage
 * key, mirroring the Portal). The rail is sticky within the page scroll; its own overflow scrolls.
 * Row 418 (19 Sep 2026): the Portal's measurements, 280 px open and 40 px collapsed
 * (OpsSlicerRail.tsx lines 58 to 102), and the same "shown of total" line under the header.
 */

/** The Portal's rail widths (OpsSlicerRail.tsx): open and collapsed. */
export const RAIL_WIDTH = 280;
export const RAIL_COLLAPSED_WIDTH = 40;

const COLLAPSE_KEY = 'mc-lookup-slicers-collapsed';

function SlicerBlock({
  axis,
  selected,
  onToggle,
  onClear,
}: {
  axis: LookupAxis;
  selected: string[];
  onToggle: (v: string) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(selected.length > 0);
  const [showAll, setShowAll] = useState(false);
  const [q, setQ] = useState('');

  if (axis.options.length === 0) return null;

  // Searchable axes (high-cardinality: ZIP / MSO / IPA / County) filter their chips by a query
  // box and cap tighter; others show up to 60 then "+N more" (Portal SlicerBlock behavior).
  const needle = q.trim().toLowerCase();
  const pool =
    axis.searchable && needle
      ? axis.options.filter((o) => (axis.labelFor ? axis.labelFor(o) : o).toLowerCase().includes(needle))
      : axis.options;
  const cap = axis.searchable ? 10 : 60;
  const visible = showAll ? pool : pool.slice(0, cap);
  const hidden = pool.length - visible.length;

  return (
    <div style={{ padding: '2px 12px' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left',
          padding: '4px 0', background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'var(--ink-900)',
        }}
      >
        <span className="muted" style={{ fontSize: 10, width: 14, flexShrink: 0 }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontSize: 12, fontWeight: 500, flex: 1 }}>{axis.label}</span>
        {selected.length > 0 && (
          <span className="pill pill-primary" style={{ fontSize: 9, padding: '2px 6px' }}>{selected.length}</span>
        )}
      </button>

      {open && (
        <div style={{ paddingLeft: 20, paddingBottom: 6 }}>
          {selected.length > 0 && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ padding: '1px 4px', fontSize: 9, marginBottom: 4 }}
              onClick={onClear}
            >
              clear {selected.length} selected
            </button>
          )}
          {axis.searchable && (
            <input
              className="text-input"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${axis.label.toLowerCase()}...`}
              style={{ width: '100%', marginBottom: 6, fontSize: 11, padding: '4px 8px' }}
            />
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {visible.map((opt) => {
              const on = selected.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  className={cn('chip', on && 'selected')}
                  style={{ padding: '3px 8px', fontSize: 10 }}
                  onClick={() => onToggle(opt)}
                  title={axis.labelFor ? axis.labelFor(opt) : opt}
                >
                  {axis.labelFor ? axis.labelFor(opt) : opt}
                </button>
              );
            })}
          </div>
          {hidden > 0 && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ padding: '1px 4px', fontSize: 9, marginTop: 4 }}
              onClick={() => setShowAll(true)}
            >
              + {hidden} more
            </button>
          )}
          {showAll && pool.length > cap && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ padding: '1px 4px', fontSize: 9, marginTop: 4, marginLeft: 6 }}
              onClick={() => setShowAll(false)}
            >
              show less
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function SlicerRail({
  axes,
  filters,
  onFilters,
  scope,
  noun,
}: {
  axes: LookupAxis[];
  filters: LookupFilters;
  onFilters: (f: LookupFilters) => void;
  /** Rows the active filter set keeps vs loaded total, shown under the header. */
  scope: { shown: number; total: number };
  noun: string;
}) {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  });
  const setCollapsedPersist = (v: boolean) => {
    setCollapsed(v);
    try {
      localStorage.setItem(COLLAPSE_KEY, v ? '1' : '0');
    } catch {
      /* storage unavailable - session-only collapse */
    }
  };

  const toggle = (axisId: string, value: string) => {
    const cur = filters[axisId] ?? [];
    const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
    onFilters({ ...filters, [axisId]: next });
  };
  const clearAxis = (axisId: string) => onFilters({ ...filters, [axisId]: [] });
  const resetAll = () => onFilters(EMPTY_LOOKUP_FILTERS);

  const activeCount = activeFilterCount(filters);

  // Contiguous same-group runs fold under one uppercase header (Portal grouping).
  const sections = useMemo(() => {
    const out: Array<{ group: string | undefined; axes: LookupAxis[] }> = [];
    for (const axis of axes) {
      const last = out[out.length - 1];
      if (last && last.group === axis.group) last.axes.push(axis);
      else out.push({ group: axis.group, axes: [axis] });
    }
    return out;
  }, [axes]);

  if (collapsed) {
    return (
      <aside aria-label="Lookup filters" style={{ flexShrink: 0, position: 'sticky', top: 12 }}>
        <button
          type="button"
          onClick={() => setCollapsedPersist(false)}
          title="Expand filters"
          aria-label="Expand filters"
          className="card"
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
            width: RAIL_COLLAPSED_WIDTH, padding: '10px 0', cursor: 'pointer',
          }}
        >
          <span className="muted" style={{ fontSize: 11 }}>◀</span>
          {activeCount > 0 && (
            <span className="pill pill-primary" style={{ fontSize: 9, padding: '2px 5px' }}>{activeCount}</span>
          )}
          <span
            className="muted"
            style={{
              fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.1em',
              textTransform: 'uppercase', writingMode: 'vertical-rl',
            }}
          >
            Filters
          </span>
        </button>
      </aside>
    );
  }

  return (
    <aside
      aria-label="Lookup filters"
      className="card card-flush"
      style={{
        width: RAIL_WIDTH, flexShrink: 0, position: 'sticky', top: 12,
        maxHeight: 'calc(100vh - 24px)', overflowY: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', borderBottom: '1px solid var(--rule)',
          position: 'sticky', top: 0, background: 'var(--paper)', zIndex: 2,
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: 'var(--ink-400)', fontWeight: 500,
          }}
        >
          Filters
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span
            className="small"
            style={{
              fontVariantNumeric: 'tabular-nums', fontSize: 10.5,
              color: activeCount > 0 ? 'var(--teal-2)' : 'var(--ink-400)',
            }}
          >
            {activeCount} active
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ padding: '2px 6px', fontSize: 9 }}
            disabled={activeCount === 0}
            onClick={resetAll}
          >
            Reset
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ padding: '2px 6px', fontSize: 10 }}
            onClick={() => setCollapsedPersist(true)}
            title="Collapse filters"
            aria-label="Collapse filters"
          >
            ▸
          </button>
        </span>
      </div>

      <div
        className="muted small"
        style={{
          padding: '6px 12px', borderBottom: '1px solid var(--rule-light, var(--rule))',
          fontVariantNumeric: 'tabular-nums', fontSize: 11,
        }}
      >
        {scope.shown.toLocaleString()} of {scope.total.toLocaleString()} {noun}
      </div>

      {sections.map((section, i) => {
        const visibleAxes = section.axes.filter((a) => a.options.length > 0);
        if (visibleAxes.length === 0) return null;
        return (
          <div
            key={section.group ?? `_ungrouped_${i}`}
            style={{ borderBottom: '1px solid var(--rule-light, var(--rule))', padding: '4px 0' }}
          >
            {section.group && (
              <div
                style={{
                  padding: '8px 12px 2px',
                  fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '0.1em',
                  textTransform: 'uppercase', color: 'var(--ink-400)', fontWeight: 500,
                }}
              >
                {section.group}
              </div>
            )}
            {visibleAxes.map((axis) => (
              <SlicerBlock
                key={axis.id}
                axis={axis}
                selected={filters[axis.id] ?? []}
                onToggle={(v) => toggle(axis.id, v)}
                onClear={() => clearAxis(axis.id)}
              />
            ))}
          </div>
        );
      })}
    </aside>
  );
}
